'use client';

import { c as _c } from "react/compiler-runtime";
import './sac-controls.scss';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.tsx';
import type { RadioGroupProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import type { SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { TrackChildSearch, trackClick } from '@sola/tracking/components/datalayer.tsx';
import type { SacQueryParams } from '../../utils/query-params.sac.tsx';
import { useSacQueryParams } from '../../utils/query-params.sac.tsx';
import { getBaseSizeProps } from '../../utils/text-utils.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
import { buildChipItems, buildCountryOptions, buildDayOptions, buildGenderOptions, buildMonthOptions, buildTrackingEvent, getSerializedSacUrl, useSacControlsState } from './sac-controls.logic.ts';
type SacControlsProps = SacFilterProps & {
  variant?: 'default' | 'stacked';
  /** When true, reads filters from URL params (dynamic). When false, uses static CMS props. */
  isDynamic?: boolean;
};

/* ─── Sub-components ─────────────────────────────────────────────────────────── */

interface SacControlsGridProps {
  className?: string;
  gender?: string;
  genderLabel: string;
  genderAriaLabel: string;
  genderOptions: RadioGroupProps['items'];
  onGenderChange: (v: React.ChangeEvent<HTMLInputElement>) => void;
  ageRange: [number, number];
  ageLabel: string;
  ageMin: number;
  ageMax: number;
  onAgeCommit: (payload: {
    newValue?: number[];
  }) => void;
  countryValue: string;
  countryLabel: string;
  countryOptions: SelectItemProps[];
  onCountryChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
}
const SacControlsGrid: React.FC<SacControlsGridProps> = t0 => {
  const $ = _c(26);
  const {
    className,
    gender,
    genderLabel,
    genderAriaLabel,
    genderOptions,
    onGenderChange,
    ageRange,
    ageLabel,
    ageMin,
    ageMax,
    onAgeCommit,
    countryValue,
    countryLabel,
    countryOptions,
    onCountryChange
  } = t0;
  const t1 = `SacControls__controls-container p-comfy-xl grid grid-cols-1 md:grid-cols-3 gap-comfy-lg ${className ?? ""}`;
  let t2;
  if ($[0] !== genderLabel) {
    t2 = <label id="gender-group-label" className="form-label label">{genderLabel}</label>;
    $[0] = genderLabel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== gender || $[3] !== genderAriaLabel || $[4] !== genderOptions || $[5] !== onGenderChange) {
    t3 = <RadioGroup defaultValue={gender} aria-label={genderAriaLabel} loop={false} variant="buttons" name="gender" aria-labelledby="gender-group-label" onChange={onGenderChange} items={genderOptions} />;
    $[2] = gender;
    $[3] = genderAriaLabel;
    $[4] = genderOptions;
    $[5] = onGenderChange;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <span>{t2}{t3}</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== ageLabel || $[11] !== ageMax || $[12] !== ageMin || $[13] !== ageRange || $[14] !== onAgeCommit) {
    t5 = <span><SliderInput value={ageRange} label={ageLabel} min={ageMin} max={ageMax} name="age" showThumbLabels={true} onCommit={onAgeCommit} /></span>;
    $[10] = ageLabel;
    $[11] = ageMax;
    $[12] = ageMin;
    $[13] = ageRange;
    $[14] = onAgeCommit;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== countryLabel || $[17] !== countryOptions || $[18] !== countryValue || $[19] !== onCountryChange) {
    t6 = <span><Select value={countryValue} label={countryLabel} name="country" onChange={onCountryChange} items={countryOptions} /></span>;
    $[16] = countryLabel;
    $[17] = countryOptions;
    $[18] = countryValue;
    $[19] = onCountryChange;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t1 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <div className={t1}>{t4}{t5}{t6}</div>;
    $[21] = t1;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
interface BirthdayControlsProps {
  birthdayLabel: string;
  birthMonth?: number;
  birthMonthAriaLabel: string;
  monthOptions: SelectItemProps[];
  onBirthMonthChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
  birthDay?: number;
  birthDayAriaLabel: string;
  dayOptions: SelectItemProps[];
  onBirthDayChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
}

// TODO: add a reset-date button
const BirthdayControls: React.FC<BirthdayControlsProps> = t0 => {
  const $ = _c(19);
  const {
    birthdayLabel,
    birthMonth,
    birthMonthAriaLabel,
    monthOptions,
    onBirthMonthChange,
    birthDay,
    birthDayAriaLabel,
    dayOptions,
    onBirthDayChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArkIcon iconName="gift-01" className="SacControls__birthday-icon hidden md:block" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== birthdayLabel) {
    t2 = <div className="label-md label-moderate">{birthdayLabel}</div>;
    $[1] = birthdayLabel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== birthMonth || $[4] !== birthMonthAriaLabel || $[5] !== monthOptions || $[6] !== onBirthMonthChange) {
    t3 = <Select value={birthMonth} name="birth month" aria-label={birthMonthAriaLabel} onChange={onBirthMonthChange} items={monthOptions} />;
    $[3] = birthMonth;
    $[4] = birthMonthAriaLabel;
    $[5] = monthOptions;
    $[6] = onBirthMonthChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== birthDay || $[9] !== birthDayAriaLabel || $[10] !== dayOptions || $[11] !== onBirthDayChange) {
    t4 = <Select value={birthDay} name="birth day" aria-label={birthDayAriaLabel} onChange={onBirthDayChange} items={dayOptions} />;
    $[8] = birthDay;
    $[9] = birthDayAriaLabel;
    $[10] = dayOptions;
    $[11] = onBirthDayChange;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <span className="flex gap-comfy-sm">{t3}{t4}</span>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t2 || $[17] !== t5) {
    t6 = <div className="flex flex-col md:flex-row items-center justify-center p-comfy-lg gap-comfy-sm">{t1}{t2}{t5}</div>;
    $[16] = t2;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
interface AdvancedOptionsProps {
  showAdvanced: boolean;
  advancedLabel: string;
  onToggle: () => void;
  children: React.ReactNode;
}
const AdvancedOptions: React.FC<AdvancedOptionsProps> = t0 => {
  const $ = _c(14);
  const {
    showAdvanced,
    advancedLabel,
    onToggle,
    children
  } = t0;
  const t1 = showAdvanced && children;
  let t2;
  if ($[0] !== showAdvanced) {
    t2 = showAdvanced ? <ArkIcon iconName="chevron-up" /> : <ArkIcon iconName="chevron-down" />;
    $[0] = showAdvanced;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== advancedLabel || $[3] !== onToggle) {
    t3 = () => {
      trackClick(advancedLabel);
      onToggle();
    };
    $[2] = advancedLabel;
    $[3] = onToggle;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== advancedLabel || $[6] !== t3) {
    t4 = <button type="button" onClick={t3}>{advancedLabel}</button>;
    $[5] = advancedLabel;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <ButtonStyle variant="tertiary" fullWidth={true} iconAfter={t2}>{t4}</ButtonStyle>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t5) {
    t6 = <div className="SacControls__adv-search-container block md:hidden flex flex-col p-comfy-xs md:p-comfy-sm justify-center">{t1}{t5}</div>;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};

/* ─── Main component ─────────────────────────────────────────────────────────── */

const InternalSacControls: React.FC<SacControlsProps & {
  sacFilters?: SacQueryParams;
  setSacFilters?: (values: Partial<SacQueryParams>) => void;
}> = t0 => {
  const $ = _c(96);
  let isDynamic;
  let props;
  let sacFilters;
  let setSacFilters;
  let t1;
  if ($[0] !== t0) {
    ({
      variant: t1,
      isDynamic,
      sacFilters,
      setSacFilters,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = isDynamic;
    $[2] = props;
    $[3] = sacFilters;
    $[4] = setSacFilters;
    $[5] = t1;
  } else {
    isDynamic = $[1];
    props = $[2];
    sacFilters = $[3];
    setSacFilters = $[4];
    t1 = $[5];
  }
  const variant = t1 === undefined ? "default" : t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["children", "core", "geo"];
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    t
  } = useTranslation(t2);
  const {
    showAdvanced,
    ageRange,
    isMultipleCountry,
    sacMinAge,
    sacMaxAge,
    handleGenderChange,
    handleAgeSliderCommit,
    handleCountryChange,
    handleBirthMonthChange,
    handleBirthDayChange,
    handleChipChange,
    toggleAdvanced
  } = useSacControlsState(sacFilters, setSacFilters);
  const t3 = sacFilters?.filters;
  let t4;
  if ($[7] !== t || $[8] !== t3) {
    t4 = buildChipItems(t, t3);
    $[7] = t;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const chips = t4;
  let t5;
  if ($[10] !== t) {
    t5 = buildGenderOptions(t);
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const genderOptions = t5;
  let t6;
  if ($[12] !== isMultipleCountry || $[13] !== t) {
    t6 = buildCountryOptions(t, isMultipleCountry);
    $[12] = isMultipleCountry;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const countryOptions = t6;
  let t7;
  if ($[15] !== t) {
    t7 = buildMonthOptions(t);
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const monthOptions = t7;
  let t8;
  if ($[17] !== t) {
    t8 = buildDayOptions(t);
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const dayOptions = t8;
  let t9;
  if ($[19] !== sacFilters) {
    t9 = buildTrackingEvent(sacFilters);
    $[19] = sacFilters;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const trackingEvent = t9;
  const showChips = variant !== "stacked";
  const t10 = sacFilters?.gender;
  let t11;
  if ($[21] !== t) {
    t11 = t("children:sac.wantToSponsor");
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t) {
    t12 = t("children:sac.controls.chooseGender");
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t) {
    t13 = t("children:sac.betweenTheAges");
    $[25] = t;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = isMultipleCountry ? "multiple" : sacFilters?.countries?.[0] ?? "";
  let t15;
  if ($[27] !== t) {
    t15 = t("children:sac.whoLivesIn");
    $[27] = t;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== ageRange || $[30] !== countryOptions || $[31] !== genderOptions || $[32] !== handleAgeSliderCommit || $[33] !== handleCountryChange || $[34] !== handleGenderChange || $[35] !== sacMaxAge || $[36] !== sacMinAge || $[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t13 || $[41] !== t14 || $[42] !== t15) {
    t16 = {
      gender: t10,
      genderLabel: t11,
      genderAriaLabel: t12,
      genderOptions,
      onGenderChange: handleGenderChange,
      ageRange,
      ageLabel: t13,
      ageMin: sacMinAge,
      ageMax: sacMaxAge,
      onAgeCommit: handleAgeSliderCommit,
      countryValue: t14,
      countryLabel: t15,
      countryOptions,
      onCountryChange: handleCountryChange
    };
    $[29] = ageRange;
    $[30] = countryOptions;
    $[31] = genderOptions;
    $[32] = handleAgeSliderCommit;
    $[33] = handleCountryChange;
    $[34] = handleGenderChange;
    $[35] = sacMaxAge;
    $[36] = sacMinAge;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  const gridProps = t16;
  let t17;
  if ($[44] !== t) {
    t17 = t("children:sac.birthdaySearch");
    $[44] = t;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const t18 = sacFilters?.birthMonth;
  let t19;
  if ($[46] !== t) {
    t19 = t("children:sac.controls.month");
    $[46] = t;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  const t20 = sacFilters?.birthDay;
  let t21;
  if ($[48] !== t) {
    t21 = t("children:sac.controls.day");
    $[48] = t;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== dayOptions || $[51] !== handleBirthDayChange || $[52] !== handleBirthMonthChange || $[53] !== monthOptions || $[54] !== t17 || $[55] !== t18 || $[56] !== t19 || $[57] !== t20 || $[58] !== t21) {
    t22 = {
      birthdayLabel: t17,
      birthMonth: t18,
      birthMonthAriaLabel: t19,
      monthOptions,
      onBirthMonthChange: handleBirthMonthChange,
      birthDay: t20,
      birthDayAriaLabel: t21,
      dayOptions,
      onBirthDayChange: handleBirthDayChange
    };
    $[50] = dayOptions;
    $[51] = handleBirthDayChange;
    $[52] = handleBirthMonthChange;
    $[53] = monthOptions;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  const birthdayProps = t22;
  const t23 = `SacControls SacControls--${variant} flex flex-col gap-roomy-xs`;
  let t24;
  if ($[60] !== t23) {
    t24 = cn(t23);
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t) {
    t25 = t("children:sac.controls.legend");
    $[62] = t;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== trackingEvent) {
    t26 = <TrackChildSearch event={trackingEvent} />;
    $[64] = trackingEvent;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== birthdayProps || $[67] !== gridProps || $[68] !== showAdvanced || $[69] !== t || $[70] !== toggleAdvanced || $[71] !== variant) {
    t27 = variant !== "stacked" && <><SacControlsGrid {...gridProps} className="hidden md:grid" /><BirthdayControls {...birthdayProps} /><AdvancedOptions showAdvanced={showAdvanced} advancedLabel={t("children:sac.advancedSearch")} onToggle={toggleAdvanced}><SacControlsGrid {...gridProps} /></AdvancedOptions></>;
    $[66] = birthdayProps;
    $[67] = gridProps;
    $[68] = showAdvanced;
    $[69] = t;
    $[70] = toggleAdvanced;
    $[71] = variant;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== gridProps || $[74] !== props || $[75] !== variant) {
    t28 = variant === "stacked" && <><FeatureBase {...props} {...getBaseSizeProps(2)} {...{
        primaryCTA: undefined,
        secondaryCTA: undefined,
        tertiaryCTA: undefined
      }} className="p-comfy-xl pb-none" inline={true} /><SacControlsGrid {...gridProps} className="flex flex-col" /></>;
    $[73] = gridProps;
    $[74] = props;
    $[75] = variant;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== isDynamic || $[78] !== sacFilters || $[79] !== t) {
    t29 = !isDynamic && <div className="p-comfy-xl"><ButtonStyle variant="primary"><Link href={getSerializedSacUrl(sacFilters)}>{t("core:actions.sponsorAChild")}</Link></ButtonStyle></div>;
    $[77] = isDynamic;
    $[78] = sacFilters;
    $[79] = t;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t27 || $[82] !== t28 || $[83] !== t29) {
    t30 = <div className="SacControls__card flex flex-col">{t27}{t28}{t29}</div>;
    $[81] = t27;
    $[82] = t28;
    $[83] = t29;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] !== chips || $[86] !== handleChipChange || $[87] !== sacFilters?.filters || $[88] !== showChips) {
    t31 = showChips && <ChipGroup chips={chips} selectedValues={sacFilters?.filters} handleSelectionChange={handleChipChange} isScrollable={true} />;
    $[85] = chips;
    $[86] = handleChipChange;
    $[87] = sacFilters?.filters;
    $[88] = showChips;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  let t32;
  if ($[90] !== t24 || $[91] !== t25 || $[92] !== t26 || $[93] !== t30 || $[94] !== t31) {
    t32 = <div className={t24} aria-label={t25} role="group">{t26}{t30}{t31}</div>;
    $[90] = t24;
    $[91] = t25;
    $[92] = t26;
    $[93] = t30;
    $[94] = t31;
    $[95] = t32;
  } else {
    t32 = $[95];
  }
  return t32;
};
export const LiveSacControls: React.FC<SacControlsProps> = props => {
  const $ = _c(17);
  const {
    isDynamic,
    gender,
    countries,
    ageMin,
    ageMax,
    disability,
    hiv,
    orphan,
    highlyVulnerable
  } = props;
  const hasDefaults = isDynamic && (gender !== undefined || countries !== undefined || ageMin !== undefined || ageMax !== undefined || disability !== undefined || hiv !== undefined || orphan !== undefined || highlyVulnerable !== undefined);
  let t0;
  if ($[0] !== ageMax || $[1] !== ageMin || $[2] !== countries || $[3] !== disability || $[4] !== gender || $[5] !== hasDefaults || $[6] !== highlyVulnerable || $[7] !== hiv || $[8] !== orphan) {
    t0 = hasDefaults ? {
      gender: gender as ChildGenderEnum | undefined,
      countries,
      ageMin,
      ageMax,
      disability,
      hiv,
      orphan,
      highlyVulnerable
    } : undefined;
    $[0] = ageMax;
    $[1] = ageMin;
    $[2] = countries;
    $[3] = disability;
    $[4] = gender;
    $[5] = hasDefaults;
    $[6] = highlyVulnerable;
    $[7] = hiv;
    $[8] = orphan;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  let t1;
  if ($[10] !== isDynamic || $[11] !== t0) {
    t1 = {
      isDynamic,
      defaults: t0
    };
    $[10] = isDynamic;
    $[11] = t0;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const {
    sacFilters,
    setSacFilters
  } = useSacQueryParams(t1);
  let t2;
  if ($[13] !== props || $[14] !== sacFilters || $[15] !== setSacFilters) {
    t2 = <InternalSacControls {...props} sacFilters={sacFilters} setSacFilters={setSacFilters} />;
    $[13] = props;
    $[14] = sacFilters;
    $[15] = setSacFilters;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
};

/** SSG fallback renders without search params; Suspense swaps in the live version. */
export const SacControls: React.FC<SacControlsProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<InternalSacControls {...props} />}><LiveSacControls {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};